import React, { useContext, useEffect, useState } from 'react';
import CardToolbar from '../lib/ui/CardToolbar';
import Card from '../lib/ui/Card';
import WithLabel from '../lib/forms/WithLabel';
import CardPadding from '../lib/ui/CardPadding';
import StudentCompetenceField from '../lib/forms/complex/StudentCompetenceField';
import UserContext from '../../context/User';
import { get, post } from '../../util/api';
import ButtonArea from '../lib/forms/ButtonArea';
import Button from '../lib/forms/simple/Button';
import CardLoadingSpinner from '../lib/ui/CardLoadingSpinner';

export default function CompetenceProfile() {
	const user = useContext(UserContext);
	const studentId = user?.studentInfo?.id;

	const [ state, setState ] = useState('LOADING');
	const [ processing, setProcessing ] = useState(false);

	const [ excludedCompetences, setExcludedCompetences ] = useState([]);
	const [ competence1, setCompetence1 ] = useState(null);
	const [ competence2, setCompetence2 ] = useState(null);
	const [ competence3, setCompetence3 ] = useState(null);
	const [ competence4, setCompetence4 ] = useState(null);
	const [ competence5, setCompetence5 ] = useState(null);

	const [ proudOfCompetence, setProudOfCompetence ] = useState(null);
	const [ proudOfExplanation, setProudOfExplanation ] = useState('');

	useEffect(() => {
		if (studentId == null) return;
		// eslint-disable-next-line
		(async () => {
			const monitor = await get(`/monitors/byStudent/${studentId}`);
			const competencesAvailable = new Set(monitor.competences.map(c => c.id));

			get(`/assessments/student/${studentId}/latest`).then(assessments => {
				const topRanked = assessments.slice().sort((a, b) => b.grade - a.grade).filter(a => competencesAvailable.has(a.competenceId));

				const filteredRanked = topRanked.filter(a => a.grade > 0);
				if (filteredRanked.length < 3) {
					setState('ERROR_NOT_ENOUGH_COMPETENCES');
				} else {
					if (filteredRanked[0] != null) setCompetence1(c1 => c1 ?? filteredRanked[0].competenceId);
					if (filteredRanked[1] != null) setCompetence2(c2 => c2 ?? filteredRanked[1].competenceId);
					if (filteredRanked[2] != null) setCompetence3(c3 => c3 ?? filteredRanked[2].competenceId);
					if (filteredRanked[3] != null) setCompetence4(c4 => c4 ?? filteredRanked[3].competenceId);
					if (filteredRanked[4] != null) setCompetence5(c5 => c5 ?? filteredRanked[4].competenceId);
					setExcludedCompetences([ ...competencesAvailable ].filter(cId => {
						const assessment = topRanked.find(tr => tr.competenceId == cId);
						return assessment == null || assessment.grade == 0;
					}));
					setState('READY');
				}
			});
		})();
	}, [ studentId ]);

	function downloadPdf() {
		const input = {
			competence1,
			competence2,
			competence3,
			competence4,
			competence5,
			proudOfCompetence,
			proudOfExplanation,
		};
		setProcessing(true);
		post(`/assessments/student/${studentId}/competenceprofile`, input, false)
			.then(result => result.blob())
			.then(blob => {
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = 'competentieprofiel.pdf';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
			})
			.finally(() => setProcessing(false));
	}

	const competencesFilled = [ competence1, competence2, competence3, competence4, competence5 ].filter(x => x != null).length;

	return <Card>
		<CardToolbar title="Competentieprofiel maken" />

		<CardPadding>
			{state == 'ERROR_NOT_ENOUGH_COMPETENCES' && <>
				<p>Je hebt nog niet genoeg competenties behaald om een competentieprofiel te kunnen maken. Zorg dat je minstens 3 competenties op niveau B hebt behaald.</p>
			</>}

			{state == 'READY' && <>
				<p>Selecteer de competenties die je graag wilt uitlichten. We hebben jouw top 5 alvast voor je ingevuld.</p>

				<WithLabel label="Competentie 1">
					<StudentCompetenceField excluding={[ ...excludedCompetences, competence2, competence3, competence4, competence5 ].filter(x => x != null)} onChange={setCompetence1} value={competence1} />
				</WithLabel>

				<WithLabel label="Competentie 2">
					<StudentCompetenceField excluding={[ ...excludedCompetences, competence1, competence3, competence4, competence5 ].filter(x => x != null)} onChange={setCompetence2} value={competence2} />
				</WithLabel>

				<WithLabel label="Competentie 3">
					<StudentCompetenceField excluding={[ ...excludedCompetences, competence1, competence2, competence4, competence5 ].filter(x => x != null)} onChange={setCompetence3} value={competence3} />
				</WithLabel>

				<WithLabel label="Competentie 4">
					<StudentCompetenceField excluding={[ ...excludedCompetences, competence1, competence2, competence3, competence5 ].filter(x => x != null)} onChange={setCompetence4} value={competence4} />
				</WithLabel>

				<WithLabel label="Competentie 5">
					<StudentCompetenceField excluding={[ ...excludedCompetences, competence1, competence2, competence3, competence4 ].filter(x => x != null)} onChange={setCompetence5} value={competence5} />
				</WithLabel>

				<br />
				<WithLabel label="Ik ben trots op">
					<StudentCompetenceField onChange={setProudOfCompetence} value={proudOfCompetence} />
				</WithLabel>

				<WithLabel label="Ik ben hier trots op omdat">
					<textarea
						style={{
							resize: 'vertical',
							minHeight: '4em',
						}}
						className="form-element"
						onChange={e => setProudOfExplanation(e.target.value)}
						value={proudOfExplanation} />
				</WithLabel>

				<br />
				<ButtonArea>
					{competencesFilled <= 2 && <span>Vul minstens 3 competenties in om door te gaan</span>}
					{competencesFilled > 2 && <Button
						onClick={downloadPdf}
						icon="cloud-download"
						label="PDF downloaden" />}
				</ButtonArea>
			</>}

			{(state == 'LOADING' || processing) && <div style={{ zIndex: 10 }}>
				<CardLoadingSpinner />
			</div>}
		</CardPadding>
	</Card>;
}